import React, { useEffect, useRef } from 'react';

const RESTRICTED_EDITOR_TYPES = ["notice_text", "warning_text"];

const RESTRICTED_TOOLBAR =
  "{bold,italic,underline,forecolor,backcolor}|{justifyleft,justifycenter,justifyright,justifyfull}|{insertorderedlist,insertunorderedlist,indent,outdent,insertblockquote,insertemoji,insertpagebreak}"
  + " #{paragraphs:toggle,fontname:toggle,fontsize:toggle,inlinestyle,lineheight}"
  + " / {removeformat,cut,copy,paste,delete,find}|{insertlink,insertvideo}|{selectall}"
  + "#{toggleborder,fullscreenenter,fullscreenexit,undo,redo,togglemore}";

function RichTextEditorComponent({ agreementContent, onContentChange, answerType }) {
  const editorRef = useRef(null);
  const editorInstanceRef = useRef(null);
  const savedRangeRef = useRef(null);
  const latestContentRef = useRef(agreementContent);
  // Track if the editor has been initialized with content
  const isInitializedRef = useRef(false);
  // Store the callback in a ref so we can update it without re-initializing the editor
  const onContentChangeRef = useRef(onContentChange);

  // Update the ref whenever the callback changes, but don't trigger re-initialization
  useEffect(() => {
    onContentChangeRef.current = onContentChange;
  }, [onContentChange]);

  useEffect(() => {
    latestContentRef.current = agreementContent;

    // The key insight: Only set HTML on the FIRST render with content.
    // After that, the editor manages its own content via the change event.
    // Never call setHTMLCode() again - that destroys cursor position.
    
    if (
      !isInitializedRef.current &&
      editorInstanceRef.current &&
      typeof agreementContent === 'string' &&
      agreementContent.trim()
    ) {
      editorInstanceRef.current.setHTMLCode(agreementContent);
      isInitializedRef.current = true;
    }
  }, [agreementContent]);

  useEffect(() => {
    let toolbarListenerCleanup = null;
    let selectionListenerCleanup = null;
    let setupTimer = null;

    const getEditorDocument = () => {
      const iframe = editorRef.current?.querySelector("iframe");
      return iframe?.contentDocument || iframe?.contentWindow?.document || null;
    };

    const getEditorWindow = () => {
      const iframe = editorRef.current?.querySelector("iframe");
      return iframe?.contentWindow || null;
    };

    const saveCurrentSelection = () => {
      const win = getEditorWindow();
      const selection = win?.getSelection?.();
      if (!selection || selection.rangeCount === 0) {
        return;
      }

      savedRangeRef.current = selection.getRangeAt(0).cloneRange();
    };

    const restoreSavedSelection = () => {
      const win = getEditorWindow();
      const selection = win?.getSelection?.();
      const savedRange = savedRangeRef.current;

      if (!win || !selection || !savedRange) {
        return false;
      }

      win.focus();
      selection.removeAllRanges();
      selection.addRange(savedRange);
      return true;
    };

    const initializeEditor = async () => {
      try {
        const Editor = await window.RichTextEditor;

        if (!Editor) {
          // console.error('RichTextEditor not initialized properly.');
          return;
        }
        if (!window.RTE_DefaultConfig) window.RTE_DefaultConfig = {};
        const isRestricted = RESTRICTED_EDITOR_TYPES.includes(answerType);
        if (isRestricted) {
          window.RTE_DefaultConfig.toolbar_notice_warning = RESTRICTED_TOOLBAR;
          window.RTE_DefaultConfig.toolbar = "notice_warning";
        } else {
          window.RTE_DefaultConfig.toolbar = "default";
        }

        editorInstanceRef.current = new Editor(editorRef.current);
        const editorInstance = editorInstanceRef.current;

        if (editorInstance && latestContentRef.current) {
          editorInstance.setHTMLCode(latestContentRef.current);
        }
        // Editor now owns its content; prevent the agreementContent effect from
        // calling setHTMLCode again (which resets the cursor) when our own change
        // event echoes the typed content back through props.
        isInitializedRef.current = true;

        editorInstance.attachEvent("change", () => {
          const updatedContent = editorInstance.getHTMLCode();
          // Call the callback using the latest reference
          if (onContentChangeRef.current) {
            onContentChangeRef.current(updatedContent);
          }
        });

        // Directly assign the file upload handler if setConfig is not available
        if (window.rte_file_upload_handler) {
          editorInstance.file_upload_handler = window.rte_file_upload_handler;
        }
        
        setupTimer = window.setTimeout(() => {
          const root = editorRef.current;
          const doc = getEditorDocument();
          const win = getEditorWindow();

          if (doc && win) {
            const selectionEvents = ["mouseup", "keyup", "touchend", "blur"];
            const handleSelectionChange = () => {
              saveCurrentSelection();
            };

            selectionEvents.forEach((eventName) => {
              doc.addEventListener(eventName, handleSelectionChange);
            });

            let pasteListenerCleanup = null;
            if (isRestricted) {
              const handlePaste = (event) => {
                event.preventDefault();
                event.stopPropagation();
                const text = (event.clipboardData || win.clipboardData).getData("text/plain");
                if (text) {
                  doc.execCommand("insertText", false, text);
                }
              };
              doc.addEventListener("paste", handlePaste, true);
              pasteListenerCleanup = () => doc.removeEventListener("paste", handlePaste, true);
            }

            selectionListenerCleanup = () => {
              selectionEvents.forEach((eventName) => {
                doc.removeEventListener(eventName, handleSelectionChange);
              });
              if (pasteListenerCleanup) pasteListenerCleanup();
            };
          }

          if (!root) {
            return;
          }

          const handleToolbarInteraction = (event) => {
            const target = event.target;
            if (!(target instanceof Element)) {
              return;
            }

            const button = target.closest(
              '[title="Insert Page Break"], [rte-tooltip="Insert Page Break"], [data-command="insertpagebreak"]'
            );

            if (!button) {
              return;
            }

            if (event.type === "mousedown") {
              event.preventDefault();
              restoreSavedSelection();
              return;
            }

            event.preventDefault();
            event.stopPropagation();
            insertPageBreakAtCursor();
          };

          root.addEventListener("mousedown", handleToolbarInteraction, true);
          root.addEventListener("click", handleToolbarInteraction, true);

          toolbarListenerCleanup = () => {
            root.removeEventListener("mousedown", handleToolbarInteraction, true);
            root.removeEventListener("click", handleToolbarInteraction, true);
          };
        }, 500);
      } catch (error) {
        // console.error('Error initializing editor:', error);
      }
    };
    
    const insertPageBreakAtCursor = () => {
      const doc = getEditorDocument();
      const win = getEditorWindow();

      if (!doc || !win) return;

      restoreSavedSelection();

      const pageBreakHtml = `
        <div className="rte-page-break" style="page-break-after: always; break-after: page; border-top: 2px dashed #bfc7d1; margin: 16px 0; height: 0;">
          <span style="display:none;">&nbsp;</span>
        </div>
        <p><br></p>
      `;

      win.focus();

      if (doc.queryCommandSupported && doc.queryCommandSupported("insertHTML")) {
        doc.execCommand("insertHTML", false, pageBreakHtml);
        saveCurrentSelection();
        window.setTimeout(() => {
          const updatedContent = editorInstanceRef.current?.getHTMLCode?.();
          if (onContentChange && typeof updatedContent === "string") {
            onContentChange(updatedContent);
          }
        }, 0);
        return;
      }

      const selection = win.getSelection();
      if (!selection || selection.rangeCount === 0) return;

      const range = selection.getRangeAt(0);
      range.deleteContents();

      const temp = doc.createElement("div");
      temp.innerHTML = pageBreakHtml;

      const fragment = doc.createDocumentFragment();
      while (temp.firstChild) {
        fragment.appendChild(temp.firstChild);
      }

      range.insertNode(fragment);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
      saveCurrentSelection();

      const updatedContent = editorInstanceRef.current?.getHTMLCode?.();
      if (onContentChange && typeof updatedContent === "string") {
        onContentChange(updatedContent);
      }
    };

    initializeEditor();

    return () => {
      if (setupTimer) {
        window.clearTimeout(setupTimer);
      }
      if (toolbarListenerCleanup) {
        toolbarListenerCleanup();
      }
      if (selectionListenerCleanup) {
        selectionListenerCleanup();
      }
      if (editorInstanceRef.current) {
        if (typeof editorInstanceRef.current.destroy === 'function') {
          editorInstanceRef.current.destroy();
        }
        editorInstanceRef.current = null;
      }
    };
    // Initialize editor only once on component mount
    // The handleContentChange callback is captured in closure and will use the latest onContentChange
  }, []);

  return <div ref={editorRef}></div>;
}

export default RichTextEditorComponent;
